<script lang="ts">
  import { onMount, onDestroy } from 'svelte';
  import { gsap } from 'gsap';

  // Login-splash hero. Inverted ancestor tree: YOU at top, parents fan DOWN.
  // Premium register — soft glow, ink-flow connectors, breathing root,
  // whisper-float on the deepest generation, shimmer pass on settle.

  export let generations: 4 | 5 = 5;
  export let viewBox: string = '0 0 400 460';
  export let trigger: number = 0;

  let root: SVGSVGElement;
  let tl: gsap.core.Timeline | null = null;
  let breathTl: gsap.core.Timeline | null = null;
  let floatTl: gsap.core.Timeline | null = null;

  type Node = { id: string; x: number; y: number; gen: number; size: number };
  type Link = { id: string; gen: number; d: string };

  function build(): { nodes: Node[]; links: Link[] } {
    const nodes: Node[] = [];
    const links: Link[] = [];
    const yByGen = [40, 110, 200, 290, 370];
    // Wider grandparent split + tighter great-great spread eliminates the
    // crowded twin-line collision in the middle of the deepest generation.
    const halfSpread = [0, 0, 130, 100, 50];
    const sizeByGen = [16, 13, 12, 10, 10];
    const xRoot = 200;

    const add = (n: Node) => { nodes.push(n); return n; };

    const you = add({ id: 'g0', x: xRoot, y: yByGen[0], gen: 0, size: sizeByGen[0] });
    const parents = add({ id: 'g1', x: xRoot, y: yByGen[1], gen: 1, size: sizeByGen[1] });
    links.push({ id: 'l0-1', gen: 1, d: `M${you.x} ${you.y} L${parents.x} ${parents.y}` });

    const gp = [
      add({ id: 'g2-l', x: xRoot - halfSpread[2], y: yByGen[2], gen: 2, size: sizeByGen[2] }),
      add({ id: 'g2-r', x: xRoot + halfSpread[2], y: yByGen[2], gen: 2, size: sizeByGen[2] }),
    ];
    gp.forEach((g, i) => {
      const midY = (parents.y + g.y) / 2;
      links.push({
        id: `l1-2-${i}`, gen: 2,
        d: `M${parents.x} ${parents.y} L${parents.x} ${midY} L${g.x} ${midY} L${g.x} ${g.y}`,
      });
    });

    const ggp: Node[] = [];
    gp.forEach((g, i) => {
      const left  = add({ id: `g3-${i}-l`, x: g.x - halfSpread[3] / 2, y: yByGen[3], gen: 3, size: sizeByGen[3] });
      const right = add({ id: `g3-${i}-r`, x: g.x + halfSpread[3] / 2, y: yByGen[3], gen: 3, size: sizeByGen[3] });
      ggp.push(left, right);
      [left, right].forEach((c, k) => {
        const midY = (g.y + c.y) / 2;
        links.push({
          id: `l2-3-${i}-${k}`, gen: 3,
          d: `M${g.x} ${g.y} L${g.x} ${midY} L${c.x} ${midY} L${c.x} ${c.y}`,
        });
      });
    });

    if (generations >= 5) {
      ggp.forEach((g, i) => {
        const left  = add({ id: `g4-${i}-l`, x: g.x - halfSpread[4] / 2, y: yByGen[4], gen: 4, size: sizeByGen[4] });
        const right = add({ id: `g4-${i}-r`, x: g.x + halfSpread[4] / 2, y: yByGen[4], gen: 4, size: sizeByGen[4] });
        [left, right].forEach((c, k) => {
          const midY = (g.y + c.y) / 2;
          links.push({
            id: `l3-4-${i}-${k}`, gen: 4,
            d: `M${g.x} ${g.y} L${g.x} ${midY} L${c.x} ${midY} L${c.x} ${c.y}`,
          });
        });
      });
    }

    return { nodes, links };
  }

  const { nodes, links } = build();

  const fillFor: Record<number, string> = {
    0: 'you', 1: 'parent', 2: 'gp', 3: 'ggp', 4: 'gggp',
  };

  function play() {
    if (!root) return;
    tl?.kill(); breathTl?.kill(); floatTl?.kill();

    const linkEls = root.querySelectorAll<SVGPathElement>('.tree-link');
    const nodeEls = root.querySelectorAll<SVGGElement>('.tree-node');
    const youGroup = root.querySelector<SVGGElement>('.you-group');
    const youGlow = root.querySelector<SVGCircleElement>('.you-glow');
    const shimmer = root.querySelector<SVGRectElement>('.shimmer');
    const wash = root.querySelector<SVGCircleElement>('.wash');

    // Init state
    gsap.set(nodeEls, { transformOrigin: '50% 50%', scale: 0, opacity: 0 });
    gsap.set(youGlow, { transformOrigin: '50% 50%', scale: 0.4, opacity: 0 });
    gsap.set(wash, { opacity: 0, scale: 0.6, transformOrigin: '50% 18%' });
    gsap.set(shimmer, { opacity: 0, attr: { y: -200 } });
    linkEls.forEach((p) => {
      const len = p.getTotalLength();
      gsap.set(p, { strokeDasharray: len, strokeDashoffset: len, opacity: 0 });
    });

    tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    // Wash fades up, then root + glow
    tl.to(wash, { opacity: 1, scale: 1, duration: 1.2, ease: 'sine.out' }, 0);
    tl.to(youGlow, { scale: 1, opacity: 1, duration: 0.9, ease: 'power2.out' }, 0.05);
    tl.to(nodeEls[0], {
      scale: 1, opacity: 1, duration: 0.7, ease: 'expo.out',
    }, 0.15);

    // Cascade per generation: ink flows down the link, node pops at the head
    const maxGen = generations >= 5 ? 4 : 3;
    for (let g = 1; g <= maxGen; g++) {
      const linksAtGen = root.querySelectorAll<SVGPathElement>(`.tree-link[data-gen="${g}"]`);
      const nodesAtGen = root.querySelectorAll<SVGGElement>(`.tree-node[data-gen="${g}"]`);
      const dur = 0.55 - g * 0.06;
      // First fade the line in slightly so it's there to draw
      tl.to(linksAtGen, { opacity: 1, duration: 0.08 }, '+=0.04');
      tl.to(linksAtGen, { strokeDashoffset: 0, duration: dur, ease: 'power2.inOut' }, '<');
      tl.to(
        nodesAtGen,
        {
          scale: 1, opacity: 1,
          duration: 0.4,
          stagger: 0.04,
          ease: 'back.out(1.8)',
        },
        `-=${dur * 0.55}`,
      );
    }

    // Shimmer sweep — a faint warm gradient passes top to bottom once.
    tl.to(shimmer, { opacity: 1, duration: 0.2 }, '+=0.1');
    tl.to(shimmer, { attr: { y: 460 }, duration: 1.1, ease: 'sine.inOut' }, '<');
    tl.to(shimmer, { opacity: 0, duration: 0.3 }, '-=0.3');

    // Persistent loops once cascade completes
    tl.add(() => startLoops(youGroup, youGlow), '>');
  }

  function startLoops(youGroup: SVGGElement | null, youGlow: SVGCircleElement | null) {
    if (!youGroup || !youGlow) return;

    // Root breath — node + glow co-pulse, slightly out of phase
    breathTl = gsap.timeline({ repeat: -1, yoyo: true, defaults: { ease: 'sine.inOut' } });
    breathTl.to(youGroup, { scale: 1.08, duration: 1.6 }, 0);
    breathTl.to(youGlow, { scale: 1.18, opacity: 0.85, duration: 1.6 }, 0);

    // Whisper float on the deepest generation — barely perceptible.
    const deepest = root.querySelectorAll<SVGGElement>(
      `.tree-node[data-gen="${generations >= 5 ? 4 : 3}"]`,
    );
    floatTl = gsap.timeline({ repeat: -1, yoyo: true, defaults: { ease: 'sine.inOut' } });
    floatTl.to(deepest, { y: 1.5, duration: 2.4, stagger: { each: 0.2, from: 'random' } }, 0);
  }

  let mounted = false;
  $: if (mounted && trigger > 0 && root) play();

  onMount(() => {
    mounted = true;
    requestAnimationFrame(() => play());
  });
  onDestroy(() => { tl?.kill(); breathTl?.kill(); floatTl?.kill(); });

  // Hover handlers — premium micro-interaction
  function onNodeEnter(e: MouseEvent) {
    gsap.to(e.currentTarget, { scale: 1.18, duration: 0.25, ease: 'power2.out', overwrite: 'auto' });
  }
  function onNodeLeave(e: MouseEvent) {
    gsap.to(e.currentTarget, { scale: 1, duration: 0.4, ease: 'power2.out', overwrite: 'auto' });
  }
</script>

<svg bind:this={root} {viewBox} class="tree" aria-hidden="true" preserveAspectRatio="xMidYMid meet">
  <defs>
    <!-- Root pigment -->
    <radialGradient id="you-pigment" cx="35%" cy="30%" r="80%">
      <stop offset="0" stop-color="#e54260"/>
      <stop offset=".55" stop-color="#c11c39"/>
      <stop offset="1" stop-color="#7a0e22"/>
    </radialGradient>
    <!-- Root glow halo -->
    <radialGradient id="you-glow-grad" cx="50%" cy="50%" r="50%">
      <stop offset="0" stop-color="rgba(193,28,57,.55)"/>
      <stop offset=".6" stop-color="rgba(193,28,57,.18)"/>
      <stop offset="1" stop-color="rgba(193,28,57,0)"/>
    </radialGradient>
    <!-- Ambient wash centred above the tree, NOT bleeding to the SVG edges -->
    <radialGradient id="wash-grad" cx="50%" cy="18%" r="55%">
      <stop offset="0" stop-color="rgba(193,28,57,.12)"/>
      <stop offset=".55" stop-color="rgba(193,28,57,.04)"/>
      <stop offset="1" stop-color="rgba(193,28,57,0)"/>
    </radialGradient>
    <!-- Connector ink — fades from elephant red at root → ochre → stone.
         userSpaceOnUse so it works on purely-vertical paths whose
         objectBoundingBox would have zero width (and render transparent). -->
    <linearGradient id="ink" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="0" y2="460">
      <stop offset="0" stop-color="#c11c39" stop-opacity="1"/>
      <stop offset=".22" stop-color="#c11c39" stop-opacity=".9"/>
      <stop offset=".45" stop-color="#bf8a40" stop-opacity=".9"/>
      <stop offset="1" stop-color="#b9a98c" stop-opacity=".75"/>
    </linearGradient>
    <!-- Node depth: subtle inner highlight gradients per gen -->
    <linearGradient id="parent-pigment" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#d6a25c"/>
      <stop offset="1" stop-color="#9a6e2d"/>
    </linearGradient>
    <linearGradient id="gp-pigment" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#c8b694"/>
      <stop offset="1" stop-color="#a8957a"/>
    </linearGradient>
    <linearGradient id="ggp-pigment" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#ece2cb"/>
      <stop offset="1" stop-color="#d8ccb6"/>
    </linearGradient>
    <linearGradient id="gggp-pigment" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#e6dcc4"/>
      <stop offset="1" stop-color="#cfc1a3"/>
    </linearGradient>
    <!-- Shimmer sweep -->
    <linearGradient id="shimmer-grad" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="rgba(255,255,255,0)"/>
      <stop offset=".5" stop-color="rgba(246,239,225,.55)"/>
      <stop offset="1" stop-color="rgba(255,255,255,0)"/>
    </linearGradient>
    <!-- Soft drop shadow for nodes -->
    <filter id="node-shadow" x="-50%" y="-50%" width="200%" height="200%">
      <feDropShadow dx="0" dy="1" stdDeviation="0.8" flood-color="#3a2a1a" flood-opacity=".22"/>
    </filter>
  </defs>

  <!-- Ambient wash (circular, no rectangular edge) -->
  <circle class="wash" cx="200" cy="80" r="240" fill="url(#wash-grad)"/>

  <!-- Connector links -->
  <g class="links">
    {#each links as l}
      <path class="tree-link" data-gen={l.gen} d={l.d} />
    {/each}
  </g>

  <!-- Nodes (each as a <g> so we can transform without affecting layout) -->
  <g class="nodes">
    {#each nodes as n}
      {#if n.gen === 0}
        <!-- Root — wrapped with its glow halo -->
        <g class="you-group" transform="translate({n.x} {n.y})">
          <circle class="you-glow" cx="0" cy="0" r="34" fill="url(#you-glow-grad)"/>
          <g class="tree-node you" data-gen="0"
             on:mouseenter={onNodeEnter} on:mouseleave={onNodeLeave}
             role="presentation">
            <rect
              x={-n.size / 2} y={-n.size / 2}
              width={n.size} height={n.size}
              rx="2.5"
              fill="url(#you-pigment)"
              filter="url(#node-shadow)"
            />
            <!-- inner highlight -->
            <rect
              x={-n.size / 2 + 2} y={-n.size / 2 + 2}
              width={n.size / 2.2} height={n.size / 4}
              rx="1.2"
              fill="rgba(255,255,255,.28)"
            />
          </g>
        </g>
      {:else}
        <g class="tree-node {fillFor[n.gen]}" data-gen={n.gen}
           transform="translate({n.x} {n.y})"
           on:mouseenter={onNodeEnter} on:mouseleave={onNodeLeave}
           role="presentation">
          <rect
            x={-n.size / 2} y={-n.size / 2}
            width={n.size} height={n.size}
            rx="2"
            fill="url(#{fillFor[n.gen]}-pigment)"
            filter="url(#node-shadow)"
          />
          <rect
            x={-n.size / 2 + 1.5} y={-n.size / 2 + 1.5}
            width={n.size / 2.4} height={n.size / 5}
            rx="1"
            fill="rgba(255,255,255,.22)"
          />
        </g>
      {/if}
    {/each}
  </g>

  <!-- Shimmer sweep (passes once on settle) -->
  <rect class="shimmer" x="0" y="-200" width="400" height="180" fill="url(#shimmer-grad)" opacity="0"/>
</svg>

<style>
  .tree { width: 100%; height: auto; display: block; overflow: visible; }

  .tree-link {
    fill: none;
    stroke: url(#ink);
    stroke-width: 1.1;
    stroke-linecap: round;
    stroke-linejoin: round;
  }
  .tree-link[data-gen="1"] { stroke-width: 1.6; }   /* you → parents: hero edge */
  .tree-link[data-gen="2"] { stroke-width: 1.3; }

  .tree-node { cursor: pointer; }
  .tree-node rect { transition: filter .2s ease; }
  .tree-node:hover rect:first-of-type {
    filter: url(#node-shadow) drop-shadow(0 0 4px rgba(193,28,57,.35));
  }
</style>
